import { useEffect, useRef } from "react";

const isBrowser = typeof window !== "undefined";

export const isDevInstrumentationEnabled =
  process.env.NODE_ENV !== "production" && isBrowser;

const now = () =>
  typeof performance !== "undefined" && typeof performance.now === "function"
    ? performance.now()
    : Date.now();

const roundDuration = (value) => Number(value.toFixed(2));

const toSerializable = (value) => {
  if (value === null || value === undefined) return value;
  if (Array.isArray(value)) return value.slice(0, 10);
  if (typeof value === "object") {
    const next = {};
    Object.entries(value).forEach(([key, entryValue]) => {
      if (
        entryValue === null ||
        entryValue === undefined ||
        typeof entryValue === "string" ||
        typeof entryValue === "number" ||
        typeof entryValue === "boolean"
      ) {
        next[key] = entryValue;
      } else if (Array.isArray(entryValue)) {
        next[key] = entryValue.slice(0, 10);
      }
    });
    return next;
  }
  return value;
};

const diffKeys = (previous, next) => {
  if (!previous || !next) return [];
  const keys = new Set([...Object.keys(previous), ...Object.keys(next)]);
  return Array.from(keys).filter((key) => previous[key] !== next[key]);
};

export const devLog = (label, payload) => {
  if (!isDevInstrumentationEnabled) return;
  if (payload === undefined) {
    console.debug(`[dev-insight] ${label}`);
    return;
  }
  console.debug(`[dev-insight] ${label}`, toSerializable(payload));
};

export const logDevPerformance = (label, payload) => {
  if (!isDevInstrumentationEnabled) return;
  console.debug(`[dev-perf] ${label}`, toSerializable(payload));
};

export const measureDevAsync = async (label, task, meta) => {
  if (!isDevInstrumentationEnabled) {
    return task();
  }

  const startedAt = now();
  try {
    const result = await task();
    logDevPerformance(label, {
      durationMs: roundDuration(now() - startedAt),
      status: "ok",
      ...(typeof meta === "function" ? meta(result) : meta),
    });
    return result;
  } catch (error) {
    logDevPerformance(label, {
      durationMs: roundDuration(now() - startedAt),
      status: "error",
      error: error?.message || String(error),
      ...(typeof meta === "function" ? meta(undefined, error) : meta),
    });
    throw error;
  }
};

export const measureDevSync = (label, task, meta) => {
  if (!isDevInstrumentationEnabled) {
    return task();
  }

  const startedAt = now();
  try {
    const result = task();
    logDevPerformance(label, {
      durationMs: roundDuration(now() - startedAt),
      status: "ok",
      ...(typeof meta === "function" ? meta(result) : meta),
    });
    return result;
  } catch (error) {
    logDevPerformance(label, {
      durationMs: roundDuration(now() - startedAt),
      status: "error",
      error: error?.message || String(error),
      ...(typeof meta === "function" ? meta(undefined, error) : meta),
    });
    throw error;
  }
};

export const useDevRenderInstrumentation = (name, snapshotFactory) => {
  const renderCountRef = useRef(0);
  const previousSnapshotRef = useRef(null);
  const renderStartedAtRef = useRef(0);

  renderCountRef.current += 1;
  renderStartedAtRef.current = now();

  const snapshot = isDevInstrumentationEnabled
    ? toSerializable(
        typeof snapshotFactory === "function" ? snapshotFactory() : snapshotFactory
      )
    : null;

  useEffect(() => {
    if (!isDevInstrumentationEnabled) return;

    const previous = previousSnapshotRef.current;
    logDevPerformance(`${name}:render`, {
      renderCount: renderCountRef.current,
      durationMs: roundDuration(now() - renderStartedAtRef.current),
      changedKeys: diffKeys(previous, snapshot),
      snapshot,
    });
    previousSnapshotRef.current = snapshot;
  });
};
